import { useField } from '@framework/form/field/useField.tsx';
import { SelectObjectInput } from '@framework/form/inputs/SelectObjectInput.tsx';
import { type DefaultOptionType } from 'antd/es/select';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { type IWidgetComponentProps } from '../../../../IWidgetType.ts';
import { useSelectOptions } from '../../common/option-list/hooks/useSelectOptions.ts';
import { getWidgetProps } from '../../common/util/getWidgetProps.ts';
import { type IMultiSelectWidget } from '../../multi-select/IMultiSelectWidget.ts';
import { multiSelectOptionsObjectName } from '../getMultiSelectOptionsObjectName.ts';
import { type IUseMultiSelectWidgetOptionsReturn } from './hooks/useMultiSelectWidgetOptions.tsx';

export interface IMultiSelectSelectComponentProps
  extends IWidgetComponentProps<IMultiSelectWidget>,
    IUseMultiSelectWidgetOptionsReturn {}

/**
 * Componente principale del widget select in caso di scelta di menù a tendina.
 */
export function MultiSelectSelectComponent(
  props: IMultiSelectSelectComponentProps,
) {
  const { widget, options, componentOptions } = props;
  const { t } = useTranslation();

  const widgetOptions = getWidgetProps(props);

  const optionsName = multiSelectOptionsObjectName(widgetOptions.name);

  const selectOptions = useSelectOptions(componentOptions, widgetOptions);

  // Quando viene selezionata una opzione, aggiorniamo direttamente nel
  // form il valore del campo.
  // Questo è fondamentale, altrimenti il valore non viene aggiornato e
  // la validazione fallisce.
  const groupFieldName = widgetOptions.name;
  const group = useField(groupFieldName);
  const groupSetValue = group.setValue;

  const handleChange = useCallback(
    (
      value: any[],
      options: DefaultOptionType | DefaultOptionType[] | undefined,
      onChange: (value: any[]) => void,
    ) => {
      groupSetValue(value, { shouldTouch: false, shouldDirty: false });
      onChange(value);
    },
    [groupSetValue],
  );

  return (
    <SelectObjectInput
      readOnly={props.readonly}
      mode="multiple"
      registerAsRef={groupFieldName}
      name={optionsName}
      options={selectOptions}
      optionFilterProp="labelText"
      onChange={handleChange}
      allowClear
      rootClassName="full-text-options"
      placeholder={
        widgetOptions.placeholder
          ? widgetOptions.placeholder
          : widgetOptions.preview
            ? t(
                'widgets.multi-select-select-component.placeholder',
                'Placeholder',
              )
            : ''
      }
      showSearch={true}
    />
  );
}
